export type CurvePoint = { x: number; y: number };

export type KatalagModelInput = {
  price: number;
  discountPct: number;
  commissionPct: number;
};

export type KatalagModelResult = {
  discountAmount: number;
  discountedPrice: number;
  commissionAmount: number;
  platformFee: number;
  processing: number;
  additionalCosts: number;
  initialNetEarnings: number;
  buyerActionBoost: number;
  sellerMotivationBoost: number;
  returnVisibilityLift: number;
  effect: number;
  effectiveNetEarnings: number;
};

export const fmt = (n: number) => `$${n.toFixed(2)}`;

export const interpolateBoost = (value: number, points: CurvePoint[]): number => {
  if (value <= points[0].x) return points[0].y;
  if (value >= points[points.length - 1].x) return points[points.length - 1].y;

  for (let i = 0; i < points.length - 1; i += 1) {
    const left = points[i];
    const right = points[i + 1];
    if (value >= left.x && value <= right.x) {
      const t = (value - left.x) / (right.x - left.x);
      return left.y + (right.y - left.y) * t;
    }
  }

  return points[points.length - 1].y;
};

export const calculateKatalagModel = ({
  price,
  discountPct,
  commissionPct,
}: KatalagModelInput): KatalagModelResult => {
  const discountedPrice = price * (1 - discountPct / 100);
  const discountAmount = price - discountedPrice;
  const commissionAmount = discountedPrice * (commissionPct / 100);
  const platformFee = discountedPrice * 0.07;
  const processing = discountedPrice * 0.029 + 0.3;
  const additionalCosts = discountAmount + commissionAmount + platformFee + processing;
  const initialNetEarnings = discountedPrice - commissionAmount - platformFee - processing;

  const buyerActionBoost = interpolateBoost(discountPct, [
    { x: 5, y: 1.0 },
    { x: 10, y: 1.05 },
    { x: 15, y: 1.09 },
    { x: 20, y: 1.07 },
    { x: 25, y: 1.03 },
    { x: 30, y: 1.0 },
  ]);

  const sellerMotivationBoost = interpolateBoost(commissionPct, [
    { x: 5, y: 1.0 },
    { x: 10, y: 1.04 },
    { x: 15, y: 1.07 },
    { x: 20, y: 1.05 },
    { x: 25, y: 1.02 },
  ]);

  const offerStrength = (discountPct + commissionPct) / 2;
  const returnVisibilityLift = interpolateBoost(offerStrength, [
    { x: 5, y: 0.9 },
    { x: 10, y: 0.98 },
    { x: 15, y: 1.05 },
    { x: 20, y: 1.02 },
    { x: 25, y: 0.97 },
  ]);

  const effect = buyerActionBoost * sellerMotivationBoost * returnVisibilityLift;
  const effectiveNetEarnings = initialNetEarnings * effect;

  return {
    discountAmount,
    discountedPrice,
    commissionAmount,
    platformFee,
    processing,
    additionalCosts,
    initialNetEarnings,
    buyerActionBoost,
    sellerMotivationBoost,
    returnVisibilityLift,
    effect,
    effectiveNetEarnings,
  };
};
